import React, { Component } from "react";
import { Modal, Spinner } from "reactstrap";
import {
  listTableBookingRequest,
  updateTablePaymentStatus,
} from "../../Endpoint/Admin/index";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import {
  C, Panel, StatCard, StatRow, SearchPill, Table, Chip, Btn,
} from "../../components/Console/ConsoleUI";
import PeriodFilter, { allTime, periodToParams, periodLabel } from "../../components/Common/PeriodFilter";
import { HOTEL_TIME_ZONE } from "../../utils/period";
import { toast } from "react-toastify";

/**
 * Table reservations — the booking half of console screen C3·08.
 *
 * Same /tableOrderList call, same detail modal, same "mark as paid"; the shape
 * follows the design's "Upcoming sittings" panel: guest, table, covers, then a
 * payment chip. The pills filter the rows already fetched, so their counts say
 * "on this page" rather than pretending to be totals.
 *
 * A slot reservation at an outlet carries no charge (§4.14), so it reads
 * "Free reservation" and stays out of the paid / awaiting-payment counts.
 */

const initials = (name) =>
  (name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0))
    .join("")
    .toUpperCase() || "?";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

/** Loyalty points as "1,250 pts"; old bookings carry none. */
const pts = (n) => `${Number(n || 0).toLocaleString()} pts`;

/**
 * The booking's loyalty lines: "Points used" (with the discount they gave),
 * "Points returned" after a cancellation, and "Points earned".
 */
const pointsLines = (b, line) => {
  const used = Number(b.pointsRedeemed) || 0;
  const value = Number(b.loyaltyDiscount) || 0;
  const back = Number(b.pointsRefunded) || 0;
  const earned = Number(b.pointsEarned) || 0;
  return (
    <React.Fragment>
      {used > 0 && line("Points used", value > 0 ? `${pts(used)} (−${money(value)})` : pts(used))}
      {back > 0 && line("Points returned", `+${pts(back)}`)}
      {earned > 0 && line("Points earned", `+${pts(earned)}`)}
    </React.Fragment>
  );
};

/** Outlet kinds as the backend stores them (models/Outlet.js). */
const OUTLET_KIND_LABEL = {
  restaurant: "Restaurant",
  cafe: "Café",
  bar: "Bar",
  lounge: "Lounge",
  poolside: "Poolside",
};

/** TableBooking.occasion values; "none" is not shown. */
const OCCASION_LABEL = {
  birthday: "Birthday",
  anniversary: "Anniversary",
  business: "Business",
  other: "Other",
};

/**
 * An outlet slot as "Sat 2 Nov, 19:30" (withYear: "Sat 2 Nov 2026, 19:30"),
 * on the HOTEL's clock rather than the browser's: the host stand reads these
 * against the restaurant's. null when there is no (valid) slot.
 */
const slotLabel = (iso, withYear) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const day = d.toLocaleDateString("en-GB", {
    timeZone: HOTEL_TIME_ZONE,
    weekday: "short",
    day: "numeric",
    month: "short",
    ...(withYear ? { year: "numeric" } : {}),
  });
  const time = d.toLocaleTimeString("en-GB", {
    timeZone: HOTEL_TIME_ZONE,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23", // not hour12:false, which can print "24:00" at midnight
  });
  return `${day.replace(/,/g, "")}, ${time}`;
};

/** "Rooftop Bar · Sat 2 Nov, 19:30", either half alone, or null on a legacy booking. */
const sittingLabel = (b) =>
  [b.outlet && b.outlet.name, slotLabel(b.slotTime)].filter(Boolean).join(" · ") || null;

/** An outlet slot reservation: free, so payment state does not apply to it. */
const isFree = (b) => Boolean(b && b.outlet);

/** The host stand's status (outlet day view); tableOrderList always sends one. */
const BOOKING_STATUS = {
  confirmed: { label: "Confirmed", tone: "good" },
  seated: { label: "Seated", tone: "warn" },
  completed: { label: "Completed", tone: "neutral" },
  no_show: { label: "No-show", tone: "bad" },
  cancelled: { label: "Cancelled", tone: "bad" },
};
const StatusChip = ({ status }) => {
  const st = BOOKING_STATUS[status];
  return st ? <Chip tone={st.tone}>{st.label}</Chip> : <span style={{ color: C.taupeLight }}>—</span>;
};

/** "Free reservation" in place of an amount and a payment chip. */
const FreeLabel = () => (
  <span style={{ fontSize: 14, color: C.taupe }}>Free reservation</span>
);

/** The controller sends "N/A" when a booking has no table (outlet bookings may not). */
const hasTable = (b) => b.tableNo != null && b.tableNo !== "N/A";

const Avatar = ({ name }) => (
  <span
    style={{
      width: 34, height: 34, borderRadius: 17, flex: "0 0 34px",
      background: C.tint, color: C.brown, display: "grid", placeItems: "center",
      fontSize: 13, fontWeight: 700,
    }}
  >
    {initials(name)}
  </span>
);

const Pager = ({ page, pages, onGo }) => {
  if (pages <= 1) return null;
  const box = (extra) => ({
    width: 34, height: 34, borderRadius: 10, display: "grid", placeItems: "center",
    fontSize: 14, cursor: "pointer", border: `1px solid ${C.border}`,
    background: "#fff", color: C.slate, ...extra,
  });
  const numbers = Array.from({ length: Math.min(5, pages) }, (_, i) => {
    if (pages <= 5) return i + 1;
    if (page <= 3) return i + 1;
    if (page >= pages - 2) return pages - 4 + i;
    return page - 2 + i;
  });
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span
        onClick={() => page > 1 && onGo(page - 1)}
        style={box({ color: page > 1 ? C.slate : "#bfae99", cursor: page > 1 ? "pointer" : "default" })}
      >
        <span className="ms" style={{ fontSize: 19 }}>chevron_left</span>
      </span>
      {numbers.map((n) => (
        <span
          key={n}
          onClick={() => onGo(n)}
          style={box(n === page ? { background: C.brown, color: "#fff", border: "none" } : {})}
        >
          {n}
        </span>
      ))}
      <span
        onClick={() => page < pages && onGo(page + 1)}
        style={box({ color: page < pages ? C.slate : "#bfae99", cursor: page < pages ? "pointer" : "default" })}
      >
        <span className="ms" style={{ fontSize: 19 }}>chevron_right</span>
      </span>
    </div>
  );
};

class TableBooking extends Component {
  constructor(props) {
    super(props);
    this.state = {
      items: [],
      isLoaded: false,
      // Pagination
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      limit: 15,
      search: "",
      // Detail modal
      showDetailModal: false,
      selectedBooking: null,
      // View-only filter over the rows already fetched.
      paymentFilter: "all",
      period: allTime(),
      breadcrumbItems: [
        { title: "Dashboard", link: "/dashboard" },
        { title: "Table Bookings", link: "#" },
      ],
    };
  }

  handlePeriodChange = (period) => {
    this.setState({ period }, () => this.getAllBookings(1));
  };

  componentDidMount() {
    this.getAllBookings();
  }

  getAllBookings = (page = this.state.currentPage, search = this.state.search) => {
    const { limit } = this.state;
    this.setState({ isLoaded: false });

    listTableBookingRequest(page, limit, search, periodToParams(this.state.period))
      .then((result) => {
        const pagination = result?.pagination || {};
        this.setState({
          isLoaded: true,
          items: result?.data || [],
          currentPage: pagination.currentPage || 1,
          totalPages: pagination.totalPages || 1,
          totalCount: pagination.totalCount || 0,
        });
      })
      .catch((error) => {
        console.error(error);
        this.setState({ isLoaded: true, items: [] });
      });
  };

  // View booking detail
  viewBookingDetail = (booking) => {
    this.setState({
      showDetailModal: true,
      selectedBooking: booking,
    });
  };

  closeDetailModal = () => {
    this.setState({
      showDetailModal: false,
      selectedBooking: null,
    });
  };

  // Update payment status
  StatusChangeRequest = (tableorderId, isPaymentSuccess) => {
    updateTablePaymentStatus(tableorderId, isPaymentSuccess)
      .then((response) => {
        if (response && response.status === 200 && response.success !== false) {
          toast.success(response.message);
          this.getAllBookings();
          this.closeDetailModal();
        } else {
          toast.error((response && response.message) || "Error updating payment status");
        }
      })
      .catch(() => toast.error("Error updating payment status"));
  };

  // Get payment badge
  getPaymentBadge = (booking) =>
    booking.isPaymentSuccess ? (
      <Chip tone="good">Paid</Chip>
    ) : (
      <Chip tone="warn">Pending</Chip>
    );

  // Get payment method label
  getPaymentMethodLabel = (type) => {
    return type === 1 ? "Cash" : "Online";
  };

  // Get person type label
  getPersonTypeLabel = (type) => {
    const t = String(type || "").toLowerCase();
    if (t === "single" || t === "couple" || t === "family") {
      return <Chip>{t.charAt(0).toUpperCase() + t.slice(1)}</Chip>;
    }
    return <Chip>{type || "N/A"}</Chip>;
  };

  visibleRows() {
    const { items, paymentFilter } = this.state;
    if (paymentFilter === "paid") return items.filter((b) => !isFree(b) && b.isPaymentSuccess);
    if (paymentFilter === "pending") return items.filter((b) => !isFree(b) && !b.isPaymentSuccess);
    return items;
  }

  columns() {
    return [
      {
        key: "createdAt",
        label: "Booked on",
        width: 190,
        render: (b) => {
          const sitting = sittingLabel(b);
          // Legacy bookings (no outlet, no slot) keep showing just the booked-on time.
          if (!sitting) return <span style={{ color: C.taupe }}>{b.createdAt}</span>;
          return (
            <div>
              <div style={{ fontSize: 14, color: C.ink }}>{sitting}</div>
              <div style={{ fontSize: 12, color: C.taupeLight }}>Booked {b.createdAt}</div>
            </div>
          );
        },
      },
      {
        key: "guest",
        label: "Guest",
        render: (b) => {
          const name = b.user || "N/A";
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
              <Avatar name={name} />
              <div>
                <div style={{ fontSize: 15, color: C.ink }}>{name}</div>
                <div style={{ fontSize: 12, color: C.taupeLight }}>
                  {b.bookingNumber ? `${b.bookingNumber} · ` : ""}
                  {b.mobile || "—"}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        key: "tableNo",
        label: "Table",
        width: 100,
        render: (b) => (
          <span
            style={{
              display: "inline-grid", placeItems: "center", minWidth: 40, height: 30,
              padding: "0 10px", borderRadius: 10, background: C.brown, color: "#fff",
              fontSize: 14, fontWeight: 500,
            }}
          >
            {hasTable(b) ? `T${b.tableNo}` : "—"}
          </span>
        ),
      },
      {
        key: "person",
        label: "Party",
        width: 110,
        render: (b) => this.getPersonTypeLabel(b.person),
      },
      {
        key: "amount",
        label: "Amount",
        width: 130,
        render: (b) => isFree(b) ? <FreeLabel /> : (
          <div>
            <div style={{ fontSize: 15, fontWeight: 500, color: C.ink }}>
              {money(b.finalAmount || 0)}
            </div>
            {b.couponCode && b.couponDiscount > 0 ? (
              <div style={{ fontSize: 12, color: "#2f7d33" }}>
                {b.couponCode} −{money(b.couponDiscount)}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        key: "payment",
        label: "Payment",
        width: 120,
        render: (b) => (isFree(b) ? <span style={{ color: C.taupeLight }}>—</span> : this.getPaymentBadge(b)),
      },
      {
        key: "status",
        label: "Status",
        width: 120,
        render: (b) => <StatusChip status={b.status} />,
      },
      {
        key: "action",
        label: "",
        width: 130,
        render: (b) => (
          <div onClick={(e) => e.stopPropagation()}>
            {isFree(b) ? null : !b.isPaymentSuccess && b.paymentMethodType === 1 ? (
              <Btn
                kind="quiet"
                style={{ height: 32, padding: "0 14px", fontSize: 13 }}
                onClick={() => this.StatusChangeRequest(b._id, true)}
              >
                Mark paid
              </Btn>
            ) : (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: C.taupeLight, fontSize: 13 }}>
                <span className="ms" style={{ fontSize: 18 }}>done_all</span>
                Complete
              </span>
            )}
          </div>
        ),
      },
    ];
  }

  renderDetailModal() {
    const { selectedBooking } = this.state;
    const line = (label, value) => (
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, fontSize: 14, color: C.slate, marginTop: 9 }}>
        <span>{label}</span>
        <span style={{ color: C.ink, textAlign: "right" }}>{value || value === 0 ? value : "N/A"}</span>
      </div>
    );

    return (
      <Modal
        isOpen={this.state.showDetailModal}
        toggle={this.closeDetailModal}
        size="lg"
        scrollable
        contentClassName="bg-transparent border-0"
          className="con-modal"
        >
        {selectedBooking && (
          <div style={{ background: "#fdf9f3", borderRadius: 22, padding: 32 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
              <span
                className="ms"
                style={{ fontSize: 26, color: C.brown, background: C.iconTint, padding: 13, borderRadius: 16 }}
              >
                event_seat
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 22, fontWeight: 500, color: C.ink, letterSpacing: "-.3px" }}>
                  {selectedBooking.bookingNumber ||
                    `#${(selectedBooking._id || "").slice(-6).toUpperCase()}`}
                </div>
                <div style={{ marginTop: 6, fontSize: 15, color: C.slate }}>
                  Booked on {selectedBooking.createdAt}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                {selectedBooking.status && <StatusChip status={selectedBooking.status} />}
                {isFree(selectedBooking) ? null : this.getPaymentBadge(selectedBooking)}
              </div>
              <span
                className="ms"
                onClick={this.closeDetailModal}
                style={{ fontSize: 22, color: C.taupeLight, cursor: "pointer" }}
              >
                close
              </span>
            </div>

            <div style={{ marginTop: 22, padding: "16px 18px", borderRadius: 14, background: C.iconTint }}>
              <div style={{ fontSize: 13, color: C.taupe, letterSpacing: ".6px" }}>GUEST</div>
              {line("Name", selectedBooking.user)}
              {line("Email", selectedBooking.email)}
              {line("Phone", selectedBooking.mobile)}
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: C.slate, marginTop: 9 }}>
                <span>Party size</span>
                {this.getPersonTypeLabel(selectedBooking.person)}
              </div>
            </div>

            <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {/* Outlet reservation fields; all absent on legacy table bookings. */}
              {selectedBooking.outlet && selectedBooking.outlet.name && (
                <div style={{ padding: "11px 13px", borderRadius: 12, background: C.surface }}>
                  <div style={{ fontSize: 12, color: C.taupeLight }}>Outlet</div>
                  <div style={{ marginTop: 4, fontSize: 15, color: C.ink }}>
                    {[
                      selectedBooking.outlet.name,
                      OUTLET_KIND_LABEL[selectedBooking.outlet.kind] || selectedBooking.outlet.kind,
                    ].filter(Boolean).join(" · ")}
                  </div>
                </div>
              )}
              {slotLabel(selectedBooking.slotTime, true) && (
                <div style={{ padding: "11px 13px", borderRadius: 12, background: C.surface }}>
                  <div style={{ fontSize: 12, color: C.taupeLight }}>Sitting</div>
                  <div style={{ marginTop: 4, fontSize: 15, color: C.ink }}>
                    {slotLabel(selectedBooking.slotTime, true)}
                  </div>
                </div>
              )}
              {selectedBooking.seatingArea && (
                <div style={{ padding: "11px 13px", borderRadius: 12, background: C.surface }}>
                  <div style={{ fontSize: 12, color: C.taupeLight }}>Seating area</div>
                  <div style={{ marginTop: 4, fontSize: 15, color: C.ink }}>{selectedBooking.seatingArea}</div>
                </div>
              )}
              {selectedBooking.occasion && selectedBooking.occasion !== "none" && (
                <div style={{ padding: "11px 13px", borderRadius: 12, background: C.surface }}>
                  <div style={{ fontSize: 12, color: C.taupeLight }}>Occasion</div>
                  <div style={{ marginTop: 4, fontSize: 15, color: C.ink }}>
                    {OCCASION_LABEL[selectedBooking.occasion] || selectedBooking.occasion}
                  </div>
                </div>
              )}
              <div style={{ padding: "11px 13px", borderRadius: 12, background: C.surface }}>
                <div style={{ fontSize: 12, color: C.taupeLight }}>Table</div>
                <div style={{ marginTop: 4, fontSize: 15, color: C.ink }}>
                  {hasTable(selectedBooking) ? `T${selectedBooking.tableNo}` : "N/A"}
                </div>
              </div>
              <div style={{ padding: "11px 13px", borderRadius: 12, background: C.surface }}>
                <div style={{ fontSize: 12, color: C.taupeLight }}>Capacity</div>
                <div style={{ marginTop: 4, fontSize: 15, color: C.ink }}>
                  {selectedBooking.tableCapacity || "N/A"}
                </div>
              </div>
              {selectedBooking.bookingDate && (
                <div style={{ padding: "11px 13px", borderRadius: 12, background: C.surface }}>
                  <div style={{ fontSize: 12, color: C.taupeLight }}>Date</div>
                  <div style={{ marginTop: 4, fontSize: 15, color: C.ink }}>{selectedBooking.bookingDate}</div>
                </div>
              )}
              {selectedBooking.bookingTime && (
                <div style={{ padding: "11px 13px", borderRadius: 12, background: C.surface }}>
                  <div style={{ fontSize: 12, color: C.taupeLight }}>Time</div>
                  <div style={{ marginTop: 4, fontSize: 15, color: C.ink }}>{selectedBooking.bookingTime}</div>
                </div>
              )}
            </div>

            {selectedBooking.specialRequests && (
              <div style={{ marginTop: 14, padding: "16px 18px", borderRadius: 14, background: C.iconTint }}>
                <div style={{ fontSize: 13, color: C.taupe, letterSpacing: ".6px" }}>SPECIAL REQUESTS</div>
                <div style={{ marginTop: 7, fontSize: 15, color: C.ink, fontStyle: "italic" }}>
                  “{selectedBooking.specialRequests}”
                </div>
              </div>
            )}

            {isFree(selectedBooking) ? (
              <div style={{ marginTop: 14, padding: "16px 18px", borderRadius: 14, background: C.iconTint }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 16, fontWeight: 500, color: C.ink }}>
                  <span>Free reservation</span>
                  <span className="ms" style={{ fontSize: 20, color: C.taupe }}>event_available</span>
                </div>
                <div style={{ marginTop: 6, fontSize: 14, color: C.slate }}>
                  A slot at the outlet; nothing is charged when booking.
                </div>
                {pointsLines(selectedBooking, line)}
              </div>
            ) : (
            <div style={{ marginTop: 14, padding: "16px 18px", borderRadius: 14, background: C.iconTint }}>
              {selectedBooking.couponCode && selectedBooking.couponDiscount > 0 && (
                <React.Fragment>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: C.slate }}>
                    <span>Subtotal</span>
                    <span style={{ color: C.ink, textDecoration: "line-through" }}>
                      {money(selectedBooking.originalAmount || selectedBooking.tableBaseAmount || 0)}
                    </span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: C.slate, marginTop: 9 }}>
                    <span>Coupon ({selectedBooking.couponCode})</span>
                    <span style={{ color: "#2f7d33" }}>−{money(selectedBooking.couponDiscount || 0)}</span>
                  </div>
                </React.Fragment>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: C.slate, marginTop: 9 }}>
                <span>Payment method</span>
                <span style={{ color: C.ink }}>
                  {this.getPaymentMethodLabel(selectedBooking.paymentMethodType)}
                </span>
              </div>
              {pointsLines(selectedBooking, line)}
              <div
                style={{
                  marginTop: 12, paddingTop: 12, borderTop: "1px solid #e6d8c4",
                  display: "flex", justifyContent: "space-between",
                  fontSize: 16, fontWeight: 500, color: C.ink,
                }}
              >
                <span>{selectedBooking.couponDiscount > 0 ? "Final total" : "Total"}</span>
                <span>{money(selectedBooking.finalAmount || 0)}</span>
              </div>
            </div>
            )}

            <div style={{ marginTop: 26, display: "flex", gap: 12, justifyContent: "flex-end" }}>
              {!isFree(selectedBooking) && !selectedBooking.isPaymentSuccess && selectedBooking.paymentMethodType === 1 && (
                <Btn onClick={() => this.StatusChangeRequest(selectedBooking._id, true)} icon="check">
                  Mark as paid
                </Btn>
              )}
              <Btn kind="quiet" onClick={this.closeDetailModal}>
                Close
              </Btn>
            </div>
          </div>
        )}
      </Modal>
    );
  }

  render() {
    const { items, isLoaded, currentPage, totalPages, totalCount, limit, search, paymentFilter } = this.state;
    const rows = this.visibleRows();
    // Free outlet reservations have no payment state, so they are in neither count.
    const charged = items.filter((b) => !isFree(b));
    const paid = charged.filter((b) => b.isPaymentSuccess).length;
    const pending = charged.length - paid;
    const pageValue = items.reduce((s, b) => s + Number(b.finalAmount || 0), 0);

    const tabs = [
      { value: "all", label: `All · ${items.length}` },
      { value: "paid", label: `Paid · ${paid}` },
      { value: "pending", label: `Awaiting payment · ${pending}` },
    ];

    return (
      <ConsolePage
        title="Table reservations"
        subtitle={`${totalCount} reservation${totalCount === 1 ? "" : "s"}${isLoaded ? "" : " · loading…"}`}
        actions={
          <React.Fragment>
            <SearchPill
              value={search}
              onChange={(v) => this.setState({ search: v })}
              placeholder="Guest, phone or table"
              width={250}
            />
            <Btn icon="search" onClick={() => this.getAllBookings(1, search)}>
              Search
            </Btn>
            {search ? (
              <Btn
                kind="quiet"
                onClick={() => this.setState({ search: "" }, () => this.getAllBookings(1, ""))}
              >
                Clear
              </Btn>
            ) : null}
          </React.Fragment>
        }
      >
        {this.renderDetailModal()}

        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <PeriodFilter value={this.state.period} onChange={this.handlePeriodChange} />
          <StatRow>
            <StatCard icon="event_seat" value={totalCount} label={this.state.period.mode === "all" ? "Reservations in total" : `Reservations · ${periodLabel(this.state.period)}`} />
            <StatCard icon="payments" value={paid} label="Paid, on this page" />
            <StatCard icon="pending_actions" value={pending} label="Awaiting payment, on this page" />
            <StatCard icon="attach_money" value={money(pageValue)} label="Value on this page" />
          </StatRow>

          <Panel style={{ padding: 0 }}>
            <div
              style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "14px 22px", borderBottom: `1px solid ${C.panel}`,
              }}
            >
              {tabs.map((t) => (
                <span
                  key={t.value}
                  onClick={() => this.setState({ paymentFilter: t.value })}
                  style={{
                    padding: "8px 15px", borderRadius: 17, fontSize: 14, cursor: "pointer",
                    background: paymentFilter === t.value ? C.brown : "transparent",
                    color: paymentFilter === t.value ? "#fff" : C.taupe,
                  }}
                >
                  {t.label}
                </span>
              ))}
              <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: C.taupe }}>
                <span>Rows</span>
                <select
                  value={limit}
                  onChange={(e) =>
                    this.setState({ limit: parseInt(e.target.value) }, () => this.getAllBookings(1))
                  }
                  style={{
                    height: 34, borderRadius: 10, border: `1px solid ${C.border}`,
                    background: "#fff", color: C.brownDeep, fontSize: 14, padding: "0 8px",
                  }}
                >
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>

            <div style={{ padding: "0 10px" }}>
              {!isLoaded ? (
                <div style={{ padding: "48px 0", textAlign: "center", color: C.taupeLight }}>
                  <Spinner style={{ color: C.brown }} />
                  <div style={{ marginTop: 10 }}>Loading bookings…</div>
                </div>
              ) : (
                <Table
                  columns={this.columns()}
                  rows={rows}
                  empty="No table bookings match this view"
                  onRowClick={this.viewBookingDetail}
                />
              )}
            </div>

            <div
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "14px 22px", borderTop: `1px solid ${C.panel}`,
              }}
            >
              <div style={{ fontSize: 14, color: C.taupeLight }}>
                Page {currentPage} of {totalPages} · {totalCount} reservation
                {totalCount === 1 ? "" : "s"}
              </div>
              <Pager page={currentPage} pages={totalPages} onGo={(p) => this.getAllBookings(p)} />
            </div>
          </Panel>
        </div>
      </ConsolePage>
    );
  }
}

export default TableBooking;
